/**
 * Organisation Schemas
 *
 * Zod schemas for validating organisation-related API responses.
 */

import { z } from 'zod';

// Country schema
export const CountrySchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().optional(),
});

export type Country = z.infer<typeof CountrySchema>;

// Countries response schema
export const CountriesResponseSchema = z.object({
  countries: z.array(CountrySchema),
});

export type CountriesResponse = z.infer<typeof CountriesResponseSchema>;

// Stock exchange schema
export const StockExchangeSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
  country: z.string(),
});

export type StockExchange = z.infer<typeof StockExchangeSchema>;

// Stock exchanges response schema
export const StockExchangesResponseSchema = z.object({
  stock_exchanges: z.array(StockExchangeSchema),
});

export type StockExchangesResponse = z.infer<typeof StockExchangesResponseSchema>;

// Band schema (for turnover, balance sheet, employees)
export const BandSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type Band = z.infer<typeof BandSchema>;

// Full organisation schema
export const OrganisationDetailSchema = z.object({
  id: z.number(),
  name: z.string(),
  registered_address: z.string().optional(),
  registered_company_number: z.string().nullable().optional(),
  registered_company_name: z.string().nullable().optional(),
  registered_country: z.string().nullable().optional(),
  registered_country_id: z.number().nullable().optional(),
  financial_year_end: z.string().nullable().optional(),
  data_residency_policy: z.string().nullable().optional(),
  operating_countries: z.array(z.object({
    id: z.number(),
    name: z.string(),
  })).optional(),
  website: z.string().nullable().optional(),
  turnover_band_id: z.number().nullable().optional(),
  balance_sheet_band_id: z.number().nullable().optional(),
  employee_band_id: z.number().nullable().optional(),
  stock_exchange_id: z.number().nullable().optional(),
  legal_entity_type: z.string().nullable().optional(),
  has_subsidiaries: z.boolean().nullable().optional(),
  primary_sector: z.string().nullable().optional(),
});

export type OrganisationDetail = z.infer<typeof OrganisationDetailSchema>;

// Organisation search results
export const OrganisationSearchResultsSchema = z.object({
  organisations: z.array(OrganisationDetailSchema),
  total: z.number().optional(),
});

export type OrganisationSearchResults = z.infer<typeof OrganisationSearchResultsSchema>;
